import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Pagination,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Dashboard as DashboardIcon,
  Business,
  Warning,
  PlayArrow,
  CheckCircle,
} from '@mui/icons-material';
import { SetWithManufacturer, Part, DashboardFilters, Pageable, AnalyzeSetRequestAnalyzeTypeEnum, BrickSetAnalyzeTypeEnum } from '../types';
import { apiService } from '../services/apiService';
import { useTokenState } from '../App';

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

// Analyse-Typ Definitionen mit Beschreibungen
const ANALYZE_TYPE_INFO = {
  [AnalyzeSetRequestAnalyzeTypeEnum.Simple]: {
    label: 'Einfach',
    description: 'Nur Stückliste erfassen - Schnelle Übersicht aller Teile ohne Schutzprüfung',
    value: 1,
  },
  [AnalyzeSetRequestAnalyzeTypeEnum.Protected]: {
    label: 'Schutzprüfung',
    description: 'Prüfung auf geschützte Steine - Identifiziert Teile mit Design-Schutz',
    value: 2,
  },
  [AnalyzeSetRequestAnalyzeTypeEnum.All]: {
    label: 'Vollständig',
    description: 'Komplette Analyse - Stückliste mit vollständiger Schutzprüfung und Details',
    value: 3,
  },
};

// Mapping von BrickSet analyzeType zu Request analyzeType
const mapBrickSetAnalyzeTypeToRequest = (type?: BrickSetAnalyzeTypeEnum): AnalyzeSetRequestAnalyzeTypeEnum | undefined => {
  if (!type) return undefined;
  switch (type) {
    case BrickSetAnalyzeTypeEnum.Simple: return AnalyzeSetRequestAnalyzeTypeEnum.Simple;
    case BrickSetAnalyzeTypeEnum.Protected: return AnalyzeSetRequestAnalyzeTypeEnum.Protected;
    case BrickSetAnalyzeTypeEnum.All: return AnalyzeSetRequestAnalyzeTypeEnum.All;
    default: return undefined;
  }
};

// Typsicherer Zugriff auf ANALYZE_TYPE_INFO
const getAnalyzeTypeInfo = (type: AnalyzeSetRequestAnalyzeTypeEnum | undefined) => {
  if (!type) return undefined;
  if (type === AnalyzeSetRequestAnalyzeTypeEnum.Simple ||
      type === AnalyzeSetRequestAnalyzeTypeEnum.Protected ||
      type === AnalyzeSetRequestAnalyzeTypeEnum.All) {
    return ANALYZE_TYPE_INFO[type];
  }
  return undefined;
};

const Dashboard: React.FC = () => {
  // Sets State
  const [sets, setSets] = useState<SetWithManufacturer[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<Pageable | null>(null);

  // Paginierung State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Filter State
  const [filters, setFilters] = useState<DashboardFilters>({
    searchTerm: '',
    manufacturer: 'all',
    status: 'all',
  });
  const [searchInput, setSearchInput] = useState(''); // Lokaler Input für debouncing

  // UI State
  const [selectedSet, setSelectedSet] = useState<SetWithManufacturer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Analyse-Dialog State
  const [analyzeDialogOpen, setAnalyzeDialogOpen] = useState(false);
  const [setToAnalyze, setSetToAnalyze] = useState<SetWithManufacturer | null>(null);
  const [selectedAnalyzeType, setSelectedAnalyzeType] = useState<AnalyzeSetRequestAnalyzeTypeEnum | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Bekannte Hersteller für Filter-Dropdown (wird aus ersten Ergebnissen extrahiert)
  const [knownManufacturers, setKnownManufacturers] = useState<string[]>([]);

  const { isTokenValid, tokenState } = useTokenState();

  // Lade Sets von der API
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const manufacturerFilter = filters.manufacturer === 'all' ? undefined : filters.manufacturer;
      const statusFilter = filters.status === 'all' ? undefined : filters.status;
      const searchFilter = filters.searchTerm || undefined;

      const data = await apiService.getSetsPageable(
        page,
        limit,
        manufacturerFilter,
        statusFilter,
        searchFilter
      );

      if (data && Array.isArray(data.items)) {
        setSets(data.items);
        setPaginationMeta(data.meta);

        // Extrahiere einzigartige Hersteller für Filter-Dropdown
        const manufacturers = [...new Set(data.items.map(s => s.manufacturerName || s.manufacturer).filter(Boolean))] as string[];
        setKnownManufacturers(prev => {
          const combined = [...new Set([...prev, ...manufacturers])];
          return combined.sort();
        });
      } else {
        console.warn('API returned invalid data format:', data);
        setSets([]);
        setPaginationMeta(null);
      }
    } catch (err) {
      console.warn('Fehler beim Laden der Daten:', err);
      setError('Fehler beim Laden der Daten');
      setSets([]);
      setPaginationMeta(null);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  // Initial laden und bei Filter-/Seitenänderung neu laden
  // Warte bis Token gültig ist, bevor API-Aufrufe gemacht werden
  useEffect(() => {
    if (!isTokenValid) {
      // Token noch nicht gültig - warte
      if (tokenState === 'idle' || tokenState === 'checking') {
        setLoading(true);
      } else {
        setLoading(false);
      }
      return;
    }

    loadData();
  }, [isTokenValid, tokenState, loadData]);

  // Debounced search - aktualisiert filters.searchTerm nach Eingabe
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.searchTerm) {
        setFilters(f => ({ ...f, searchTerm: searchInput }));
        setPage(1); // Reset zur ersten Seite bei neuer Suche
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, filters.searchTerm]);

  const handleViewDetails = (set: SetWithManufacturer) => {
    setSelectedSet(set);
  };

  const handleCloseModal = () => {
    setSelectedSet(null);
  };

  // Analyse-Dialog öffnen
  const handleOpenAnalyzeDialog = (set: SetWithManufacturer) => {
    setSetToAnalyze(set);
    setSelectedAnalyzeType(null);
    setAnalyzeDialogOpen(true);
  };

  // Analyse-Dialog schließen
  const handleCloseAnalyzeDialog = () => {
    setAnalyzeDialogOpen(false);
    setSetToAnalyze(null);
    setSelectedAnalyzeType(null);
  };

  // Verfügbare Analyse-Typen basierend auf aktuellem analyzeType des Sets
  const getAvailableAnalyzeTypes = (currentType?: BrickSetAnalyzeTypeEnum): AnalyzeSetRequestAnalyzeTypeEnum[] => {
    const currentRequestType = mapBrickSetAnalyzeTypeToRequest(currentType);
    const currentInfo = getAnalyzeTypeInfo(currentRequestType);
    const currentValue = currentInfo?.value || 0;

    return Object.entries(ANALYZE_TYPE_INFO)
      .filter(([_, info]) => info.value > currentValue)
      .map(([type]) => type as AnalyzeSetRequestAnalyzeTypeEnum);
  };

  // Analyse starten
  const handleStartAnalysis = async () => {
    if (!setToAnalyze || !selectedAnalyzeType) return;

    try {
      setAnalyzing(true);
      await apiService.analyzeSet(setToAnalyze.id, selectedAnalyzeType);
      handleCloseAnalyzeDialog();
      // Daten neu laden
      loadData();
    } catch (err) {
      console.error('Fehler beim Starten der Analyse:', err);
      setError('Fehler beim Starten der Analyse');
    } finally {
      setAnalyzing(false);
    }
  };

  const resetFilters = () => {
    setSearchInput('');
    setFilters({
      searchTerm: '',
      manufacturer: 'all',
      status: 'all',
    });
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event: SelectChangeEvent<number>) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleManufacturerChange = (event: SelectChangeEvent<string>) => {
    setFilters(f => ({ ...f, manufacturer: event.target.value }));
    setPage(1);
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setFilters(f => ({ ...f, status: event.target.value as DashboardFilters['status'] }));
    setPage(1);
  };

  // Statistiken aus Pagination-Meta
  const totalSets = paginationMeta?.totalItems ?? sets.length;
  // protectedBricks ist jetzt String (Zahl oder "ANALYZING")
  const unsafeSets = sets.filter(s => s.protectedBricks !== '0' && s.protectedBricks !== 'ANALYZING').length;

  // Loading state
  if (loading) {
    return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
        <Container maxWidth="xl">
          <Card elevation={2}>
            <CardContent sx={{ py: 8, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Daten werden geladen...
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
        <Container maxWidth="xl">
          <Card elevation={2}>
            <CardContent sx={{ py: 8, textAlign: 'center' }}>
              <Warning sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" color="error" gutterBottom>
                Fehler beim Laden
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Übersicht aller analysierten Sets und Hersteller
          </Typography>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DashboardIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Gesamt-Sets
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {totalSets}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Business sx={{ color: 'info.main', mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Hersteller
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {knownManufacturers.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Warning sx={{ color: 'warning.main', mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Unsichere Sets (auf Seite)
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {unsafeSets}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Sets durchsuchen..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Hersteller</InputLabel>
                  <Select
                    value={filters.manufacturer}
                    onChange={handleManufacturerChange}
                    label="Hersteller"
                  >
                    <MenuItem value="all">Alle Hersteller</MenuItem>
                    {knownManufacturers.map((mfr) => (
                      <MenuItem key={mfr} value={mfr}>
                        {mfr}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={handleStatusChange}
                    label="Status"
                  >
                    <MenuItem value="all">Alle Status</MenuItem>
                    <MenuItem value="safe">Sicher</MenuItem>
                    <MenuItem value="unsafe">Unsicher</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button fullWidth variant="outlined" onClick={resetFilters} startIcon={<FilterList />}>
                  Zurücksetzen
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Sets List */}
        <Card elevation={2}>
          <CardContent sx={{ p: 0 }}>
            {sets.length === 0 ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  Keine Sets gefunden
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Passen Sie Ihre Suchkriterien an
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600, py: 2 }}>Set-Nummer</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 2 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 2 }}>Hersteller</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 2 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 2 }} align="right">Teile gesamt</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 2 }} align="right">Geschützt</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 2 }}>Analysiert am</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 2 }} align="center">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sets.map((set) => (
                        <TableRow
                          key={set.id}
                          hover
                          onClick={() => handleViewDetails(set)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            borderLeft: set.protectedBricks !== '0' && set.protectedBricks !== 'ANALYZING'
                              ? '4px solid'
                              : '4px solid transparent',
                            borderColor: set.protectedBricks !== '0' && set.protectedBricks !== 'ANALYZING'
                              ? 'error.main'
                              : 'transparent',
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={set.setNumber}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {set.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {set.manufacturerName || set.manufacturer}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                set.protectedBricks === 'ANALYZING'
                                  ? 'Wird analysiert'
                                  : set.protectedBricks === '0'
                                  ? 'Sicher'
                                  : 'Unsicher'
                              }
                              color={
                                set.protectedBricks === 'ANALYZING'
                                  ? 'warning'
                                  : set.protectedBricks === '0'
                                  ? 'success'
                                  : 'error'
                              }
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {set.totalBricks.toLocaleString('de-DE')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={
                                set.protectedBricks !== '0' && set.protectedBricks !== 'ANALYZING'
                                  ? 'error.main'
                                  : 'text.secondary'
                              }
                              fontWeight={
                                set.protectedBricks !== '0' && set.protectedBricks !== 'ANALYZING'
                                  ? 600
                                  : 400
                              }
                            >
                              {set.protectedBricks === 'ANALYZING' ? '—' : set.protectedBricks}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(set.analyzedDate).toLocaleDateString('de-DE')}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => handleViewDetails(set)}
                                startIcon={<Visibility />}
                              >
                                Details
                              </Button>
                              {getAvailableAnalyzeTypes(set.analyzeType).length > 0 ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  onClick={() => handleOpenAnalyzeDialog(set)}
                                  startIcon={<PlayArrow />}
                                >
                                  Analysieren
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled
                                  startIcon={<CheckCircle />}
                                  sx={{
                                    borderColor: 'success.main',
                                    color: 'success.main',
                                    '&.Mui-disabled': {
                                      borderColor: 'success.main',
                                      color: 'success.main',
                                      opacity: 0.7,
                                    },
                                  }}
                                >
                                  Vollständig
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {paginationMeta && paginationMeta.totalPages > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 3,
                      py: 2,
                      bgcolor: 'grey.50',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="sets-per-page-label">Pro Seite</InputLabel>
                        <Select
                          labelId="sets-per-page-label"
                          value={limit}
                          label="Pro Seite"
                          onChange={handleLimitChange}
                        >
                          {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {paginationMeta.pagingCounter ?? ((page - 1) * limit + 1)} -{' '}
                        {Math.min(
                          (paginationMeta.pagingCounter ?? ((page - 1) * limit + 1)) + sets.length - 1,
                          paginationMeta.totalItems
                        )}{' '}
                        von {paginationMeta.totalItems}
                      </Typography>
                    </Box>

                    <Pagination
                      count={paginationMeta.totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                      siblingCount={1}
                      boundaryCount={1}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Details Modal */}
      <Dialog
        open={Boolean(selectedSet)}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        {selectedSet && (
          <>
            <DialogTitle sx={{ bgcolor: 'grey.50', pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      label={selectedSet.setNumber}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Typography variant="h6" fontWeight={600}>
                      {selectedSet.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    <Business sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    {selectedSet.manufacturerName || selectedSet.manufacturer} • Analysiert am {new Date(selectedSet.analyzedDate).toLocaleDateString('de-DE')}
                  </Typography>
                </Box>
                <Chip
                  label={
                    selectedSet.protectedBricks === 'ANALYZING'
                      ? 'Wird analysiert'
                      : selectedSet.protectedBricks === '0'
                      ? 'Sicher'
                      : 'Unsicher'
                  }
                  color={
                    selectedSet.protectedBricks === 'ANALYZING'
                      ? 'warning'
                      : selectedSet.protectedBricks === '0'
                      ? 'success'
                      : 'error'
                  }
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Analyse-Typ */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Analyse-Typ
                  </Typography>
                  {getAvailableAnalyzeTypes(selectedSet.analyzeType).length > 0 && (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      startIcon={<PlayArrow />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseModal();
                        handleOpenAnalyzeDialog(selectedSet);
                      }}
                    >
                      Upgrade
                    </Button>
                  )}
                </Box>
                {selectedSet.analyzeType ? (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {Object.entries(ANALYZE_TYPE_INFO)
                      .filter(([_, info]) => {
                        const currentRequestType = mapBrickSetAnalyzeTypeToRequest(selectedSet.analyzeType);
                        const currentInfo = getAnalyzeTypeInfo(currentRequestType);
                        const currentValue = currentInfo?.value || 0;
                        // Nur aktuellen und höherwertige Typen anzeigen
                        return info.value >= currentValue;
                      })
                      .map(([type, info], index, filteredArray) => {
                        const currentRequestType = mapBrickSetAnalyzeTypeToRequest(selectedSet.analyzeType);
                        const currentInfo = getAnalyzeTypeInfo(currentRequestType);
                        const currentValue = currentInfo?.value || 0;
                        const isCurrent = info.value === currentValue;

                        return (
                          <React.Fragment key={type}>
                            <Box
                              sx={{
                                flex: 1,
                                p: 1.5,
                                borderRadius: 1,
                                border: '2px solid',
                                borderColor: isCurrent ? 'success.main' : 'divider',
                                bgcolor: isCurrent ? 'success.50' : 'background.paper',
                                position: 'relative',
                                transition: 'all 0.3s',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                {isCurrent && (
                                  <CheckCircle
                                    sx={{
                                      fontSize: 16,
                                      color: 'success.main',
                                    }}
                                  />
                                )}
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color={isCurrent ? 'success.main' : 'text.secondary'}
                                >
                                  {info.label}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.7rem', display: 'block' }}
                              >
                                {info.description.split(' - ')[0]}
                              </Typography>
                              {isCurrent && (
                                <Chip
                                  label="Durchgeführt"
                                  size="small"
                                  color="success"
                                  sx={{ mt: 1, height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                />
                              )}
                            </Box>
                            {index < filteredArray.length - 1 && (
                              <Box
                                sx={{
                                  width: 24,
                                  height: 2,
                                  bgcolor: 'divider',
                                }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                  </Box>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="info.main" fontWeight={500}>
                      Keine Analyse-Information verfügbar
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      analyzeType: {String(selectedSet.analyzeType || 'undefined')}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Statistiken */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                      Teile gesamt
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                      {selectedSet.totalBricks.toLocaleString('de-DE')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: selectedSet.protectedBricks !== '0' && selectedSet.protectedBricks !== 'ANALYZING'
                        ? 'error.50'
                        : 'grey.50',
                      borderColor: selectedSet.protectedBricks !== '0' && selectedSet.protectedBricks !== 'ANALYZING'
                        ? 'error.main'
                        : 'divider',
                      borderWidth: selectedSet.protectedBricks !== '0' && selectedSet.protectedBricks !== 'ANALYZING'
                        ? 2
                        : 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                      Geschützte Teile
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ mt: 0.5 }}
                      color={selectedSet.protectedBricks !== '0' && selectedSet.protectedBricks !== 'ANALYZING' ? 'error.main' : 'text.primary'}
                    >
                      {selectedSet.protectedBricks === 'ANALYZING' ? '—' : selectedSet.protectedBricks}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Stückliste
              </Typography>

              {selectedSet.partsList && selectedSet.partsList.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Teilenummer</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Farbe</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Anzahl</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Echte Parts anzeigen */}
                      {selectedSet.partsList.map((part: Part, index: number) => (
                        <TableRow
                          key={index}
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            borderLeft: part.protectionStatus === 'PROTECTED'
                              ? '3px solid'
                              : '3px solid transparent',
                            borderColor: part.protectionStatus === 'PROTECTED'
                              ? 'error.main'
                              : 'transparent',
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                              {part.partNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{part.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {part.color}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={500}>
                              {part.quantity}×
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                part.protectionStatus === 'PROTECTED' ? 'Geschützt' :
                                part.protectionStatus === 'ANALYZING' ? 'Wird analysiert' : 'Sicher'
                              }
                              color={
                                part.protectionStatus === 'PROTECTED' ? 'error' :
                                part.protectionStatus === 'ANALYZING' ? 'warning' : 'success'
                              }
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Dummy-Parts mit blur-Effekt wenn totalPartsCount größer als partsList.length */}
                      {selectedSet.totalPartsCount && selectedSet.totalPartsCount > selectedSet.partsList.length && (
                        Array.from({ length: Math.min(selectedSet.totalPartsCount - selectedSet.partsList.length, 10) }).map((_, index) => (
                          <TableRow
                            key={`dummy-${index}`}
                            sx={{
                              filter: 'blur(4px)',
                              opacity: 0.5,
                              userSelect: 'none',
                              pointerEvents: 'none',
                            }}
                          >
                            <TableCell>••••••</TableCell>
                            <TableCell>••••••••••••</TableCell>
                            <TableCell>••••••</TableCell>
                            <TableCell align="right">••x</TableCell>
                            <TableCell>
                              <Chip
                                label="••••••"
                                color="default"
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  {/* Hinweis wenn mehr Parts vorhanden sind */}
                  {selectedSet.totalPartsCount && selectedSet.totalPartsCount > selectedSet.partsList.length && (
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedSet.totalPartsCount - selectedSet.partsList.length} weitere Teile werden nach Abschluss der Analyse angezeigt
                      </Typography>
                    </Box>
                  )}
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Keine detaillierte Stückliste verfügbar.
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
              <Button onClick={handleCloseModal} variant="contained">
                Schließen
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Analyse-Dialog */}
      <Dialog
        open={analyzeDialogOpen}
        onClose={handleCloseAnalyzeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayArrow color="secondary" />
            Analyse starten
          </Box>
        </DialogTitle>
        <DialogContent>
          {setToAnalyze && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Set: <strong>{setToAnalyze.name}</strong> ({setToAnalyze.setNumber})
              </Typography>

              {setToAnalyze.analyzeType && (
                <Chip
                  label={`Aktuelle Analyse: ${getAnalyzeTypeInfo(mapBrickSetAnalyzeTypeToRequest(setToAnalyze.analyzeType))?.label || 'Unbekannt'}`}
                  size="small"
                  color="info"
                  sx={{ mb: 2 }}
                />
              )}

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Wählen Sie den Analyse-Typ:
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {getAvailableAnalyzeTypes(setToAnalyze.analyzeType).map((type) => {
                  const info = getAnalyzeTypeInfo(type);
                  if (!info) return null;
                  const isSelected = selectedAnalyzeType === type;
                  return (
                    <Paper
                      key={type}
                      variant={isSelected ? 'elevation' : 'outlined'}
                      elevation={isSelected ? 3 : 0}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'secondary.main' : 'divider',
                        bgcolor: isSelected ? 'secondary.50' : 'background.paper',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'secondary.main',
                          bgcolor: 'secondary.50',
                        },
                      }}
                      onClick={() => setSelectedAnalyzeType(type)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {isSelected && <CheckCircle color="secondary" fontSize="small" />}
                        <Typography variant="subtitle1" fontWeight={600}>
                          {info.label}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {info.description}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>

              {getAvailableAnalyzeTypes(setToAnalyze.analyzeType).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Dieses Set wurde bereits vollständig analysiert.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAnalyzeDialog} disabled={analyzing}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleStartAnalysis}
            disabled={!selectedAnalyzeType || analyzing}
            startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
          >
            {analyzing ? 'Wird gestartet...' : 'Analyse starten'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
